import React, { useState } from 'react';
import { PUBLIC_CONTACT_EMAIL } from '../../constants/landingPublicContact';
import styles from './AccessibilityStatement.module.css';

interface AccessibilityStatementProps {
  className?: string;
}

const AccessibilityStatement: React.FC<AccessibilityStatementProps> = ({ 
  className = '' 
}) => {
  const [showModal, setShowModal] = useState(false);

  const handleLinkClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  return (
    <>
      <div className={className}>
        <a 
          href="#"
          onClick={handleLinkClick}
          style={{ 
            color: '#fff', 
            textDecoration: 'none', 
            fontSize: '0.95rem',
            lineHeight: '1'
          }}
        >
          Accessibility Statement
        </a>
      </div>

      {showModal && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Accessibility Statement – Tranzit.io</h2>
              <button 
                className={styles.closeButton}
                onClick={closeModal}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            
            <div className={styles.modalBody}>
              <div className={styles.section}>
                <p><strong>Effective Date:</strong> April 2025</p>
                
                <p>
                  Tranzit.io is owned and operated by SMR Consulting LLC (doing business as Tranzit.io), located in Houston, Texas.
                  We are committed to providing a digital experience that is accessible to the widest possible audience, including
                  individuals with disabilities.
                </p>

                <h3>1. Commitment to Accessibility</h3>
                <p>
                  We make reasonable efforts to design and maintain our platform in alignment with the Web Content Accessibility
                  Guidelines (WCAG) 2.1 Level AA, where feasible. Tranzit.io is a complex, data-driven SaaS platform with dynamic and
                  real-time features, and certain elements may not fully conform to accessibility standards at all times. We remain
                  committed to ongoing evaluation and improvement.
                </p>

                <h3>2. Measures We Take</h3>
                <p>
                  Our accessibility efforts may include internal accessibility reviews, use of semantic HTML structures where practicable,
                  consideration of color contrast best practices, scalable typography, logical keyboard navigation where applicable,
                  captioning or transcripts for instructional content when provided, and accessibility considerations during new feature
                  development.
                </p>

                <h3>3. Assistive Technology Compatibility</h3>
                <p>
                  We aim to support commonly used assistive technologies, including screen readers, keyboard navigation, browser
                  zoom and text resizing, high-contrast display settings, and screen magnification tools. Compatibility may vary
                  depending on device, browser, operating system, or third-party technology.
                </p>

                <h3>4. Third-Party Content and Integrations</h3>
                <p>
                  Tranzit.io may integrate third-party services such as mapping tools, authentication providers, document viewers,
                  analytics, and AI-driven components. We do not control and cannot guarantee the accessibility of third-party content or
                  technologies.
                </p>

                <h3>5. Limitations and Disclaimers</h3>
                <p>
                  Certain features, including interactive maps, dashboards, AI-generated content, and real-time data visualizations, may
                  not fully meet all accessibility standards. We do not warrant uninterrupted accessibility or compatibility with all assistive
                  technologies at all times.
                </p>

                <h3>6. Continuous Improvement</h3>
                <p>
                  We review accessibility considerations as part of ongoing development and platform updates. Feedback regarding
                  accessibility is welcomed and considered in future improvements.
                </p>

                <h3>7. Reporting Accessibility Issues</h3>
                <p>
                  If you experience accessibility difficulties, please contact us:
                </p>
                <p>
                  SMR Consulting LLC (DBA Tranzit.io)<br />
                  Houston, Texas, United States<br />
                  Email: {PUBLIC_CONTACT_EMAIL}
                </p>
                <p>
                  Please include, where possible, a description of the issue, the page or feature involved, and the device, browser, or
                  assistive technology used.
                </p>

                <h3>8. No Warranty</h3>
                <p>
                  This Accessibility Statement does not create any express or implied warranties and does not impose obligations beyond
                  those required by applicable law.
                </p>
              </div>
            </div>
            
            <div className={styles.modalFooter}>
              <button 
                className={styles.closeButtonFooter}
                onClick={closeModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AccessibilityStatement;






